import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, normalizeClass as _normalizeClass, resolveComponent as _resolveComponent, createVNode as _createVNode, normalizeStyle as _normalizeStyle } from "vue"

const _hoisted_1 = {
  class: "w-mobile-days",
  "aria-label": "Дни недели"
}
const _hoisted_2 = ["aria-pressed", "aria-label", "onClick"]
const _hoisted_3 = {
  class: "w-table",
  "aria-label": "Недельный календарь приёма: время по строкам, дни по столбцам"
}
const _hoisted_4 = ["aria-label", "onClick"]
const _hoisted_5 = { class: "w-day-hours" }
const _hoisted_6 = { class: "w-hours-value" }
const _hoisted_7 = { class: "w-hours-value" }
const _hoisted_8 = { key: 0 }
const _hoisted_9 = ["colspan"]
const _hoisted_10 = {
  scope: "row",
  class: "w-time"
}
const _hoisted_11 = ["rowspan", "data-range-date", "data-range-start", "data-range-end"]
const _hoisted_12 = ["data-color"]
const _hoisted_13 = ["data-room-slot", "aria-label", "aria-expanded", "onClick"]
const _hoisted_14 = { class: "w-room-num" }
const _hoisted_15 = ["aria-label", "onClick"]
const _hoisted_16 = { class: "w-status-full" }
const _hoisted_17 = {
  class: "w-status-short",
  "aria-hidden": "true"
}
const _hoisted_18 = { class: "w-slot-time" }
const _hoisted_19 = ["aria-label", "onClick"]
const _hoisted_20 = ["disabled", "aria-label", "onClick"]
const _hoisted_21 = ["aria-label"]
const _hoisted_22 = { class: "w-end-row" }
const _hoisted_23 = {
  scope: "row",
  class: "w-time"
}

import {
  computed, onBeforeUnmount, onMounted, ref,
} from 'vue';
import { useScheduleStore } from '@/store/calendar/scheduleModule';
import {
  checkBranchAccess,
  formatDate,
  formatDayTitle,
  hoursText,
  isWeekend,
  toMinutes,
  toTime,
} from '~utils/calendarModel';
import type {
  CalendarBreak, CellColor, Slot, TimeInterval,
} from '@/store/calendar/types';

type SlotStatus = {
  color: CellColor;
  label: string;
  full: string;
  short: string;
};

type CellBase = {
  day: string;
  start: string;
  end: string;
  rowspan: number;
  height: number;
};

type GridCell = CellBase & (
  | { kind: 'slot'; slot: Slot; status: SlotStatus }
  | { kind: 'break'; rest: CalendarBreak }
  | { kind: 'empty' }
  | { kind: 'closed'; reason: string; text: string }
);

type GridRow = {
  start: string;
  height: number;
  cells: GridCell[];
};

type Range = TimeInterval & { date: string };

type DragState = Range & {
  x: number;
  y: number;
  moved: boolean;
};

// Высота получасового интервала, px; короче строка не бывает
const HALF_HOUR_HEIGHT = 26;
const GRID_STEP = 30;
const DRAG_THRESHOLD = 6;
const CLICK_SUPPRESS_MS = 200;


export default /*@__PURE__*/_defineComponent({
  __name: 'WeekGrid',
  setup(__props) {

const store = useScheduleStore();
const tableWrap = ref<HTMLElement | null>(null);

const dayHeads = computed(() => store.displayDays.map((day) => ({
  day,
  hours: store.branchDay(day),
})));

const ownSlots = computed(() => store.schedule.slots.filter(
  (slot) => slot.staffId === store.staffId,
));

const ownBreaks = computed(() => store.schedule.breaks.filter(
  (rest) => rest.staffId === store.staffId,
));

// Границы строк: сетка по 30 минут плюс все начала/концы приёмов, перерывов
// и интервалов режима филиала — чтобы каждый элемент вставал ровно в свои строки
const timeMarks = computed<number[]>(() => {
  const { from, to } = store.viewHours;
  if (!from || !to) return [];
  const low = toMinutes(from);
  const high = toMinutes(to);
  const marks = new Set([low, high]);
  const addMark = (value: string) => {
    const minute = toMinutes(value);
    if (minute > low && minute < high) marks.add(minute);
  };

  for (let minute = Math.ceil(low / GRID_STEP) * GRID_STEP; minute < high; minute += GRID_STEP) {
    marks.add(minute);
  }
  [...ownSlots.value, ...ownBreaks.value]
    .filter((item) => store.displayDays.includes(item.date))
    .forEach((item) => {
      addMark(item.start);
      addMark(item.end);
    });
  store.displayDays.forEach((day) => {
    const { open, intake } = store.branchDay(day);
    [...open, ...intake].forEach((item) => {
      addMark(item.start);
      addMark(item.end);
    });
  });

  return [...marks].sort((a, b) => a - b);
});

const rowHeights = computed(() => timeMarks.value.slice(0, -1).map((minute, index) => (
  Math.max(HALF_HOUR_HEIGHT, ((timeMarks.value[index + 1] - minute) * HALF_HOUR_HEIGHT) / GRID_STEP)
)));

const getSlotStatus = (slot: Slot): SlotStatus => {
  const availability = store.slotAvailability(slot);
  const { color, branchBlocked } = availability;
  const ownPatient = store.schedule.appointments.some((item) => item.slotId === slot.id);
  const closedText = branchBlocked ? 'Вне приёма' : 'Закрыт';
  const statuses: Record<CellColor, Omit<SlotStatus, 'color'>> = {
    red: { label: ownPatient ? 'Пациент' : 'Пациент коллеги', full: 'Пациент', short: 'Запись' },
    gray: {
      label: branchBlocked ? 'Вне приёма' : 'Кабинет закрыт',
      full: closedText,
      short: 'Закрыт',
    },
    yellow: { label: 'Коллеги', full: 'Коллеги', short: 'Колл.' },
    green: { label: 'Свободно', full: 'Свободно', short: 'Своб.' },
  };
  return { color, ...statuses[color] };
};

const covers = (item: TimeInterval & { date: string }, day: string, minute: number) => (
  item.date === day && toMinutes(item.start) <= minute && toMinutes(item.end) > minute
);

const buildCell = (day: string, index: number): GridCell | null => {
  const marks = timeMarks.value;
  const minute = marks[index];
  const low = marks[0];
  const high = marks[marks.length - 1];
  const slot = ownSlots.value.find((item) => covers(item, day, minute));
  const rest = ownBreaks.value.find((item) => covers(item, day, minute));
  const item = slot || rest;

  // Ячейку уже заняла предыдущая строка через rowspan
  if (item && Math.max(toMinutes(item.start), low) !== minute) return null;

  const finish = item ? Math.min(toMinutes(item.end), high) : marks[index + 1];
  const lastIndex = marks.indexOf(finish);
  const base: CellBase = {
    day,
    start: toTime(minute),
    end: toTime(finish),
    rowspan: lastIndex - index,
    height: rowHeights.value.slice(index, lastIndex).reduce((sum, value) => sum + value, 0) - 3,
  };

  if (slot) {
    return {
      ...base, kind: 'slot', slot, status: getSlotStatus(slot),
    };
  }
  if (rest) return { ...base, kind: 'break', rest };

  const access = checkBranchAccess(store.schedule, store.rooms, {
    date: day,
    start: base.start,
    end: base.end,
    roomId: store.defaultRoomId,
  });
  if (access.ok) return { ...base, kind: 'empty' };

  const duringOpen = store.branchDay(day).open.some(
    (interval) => toMinutes(interval.start) <= minute && toMinutes(interval.end) >= finish,
  );
  return {
    ...base,
    kind: 'closed',
    reason: access.message,
    text: duringOpen ? 'Нет приёма' : 'Закрыто',
  };
};

const rows = computed<GridRow[]>(() => timeMarks.value.slice(0, -1).map((minute, index) => ({
  start: toTime(minute),
  height: rowHeights.value[index],
  cells: store.displayDays
    .map((day) => buildCell(day, index))
    .filter((cell): cell is GridCell => cell !== null),
})));

const breakIcon = (label: string) => (label.toLowerCase().includes('проветр') ? 'air' : 'coffee');

const isSelected = (kind: 'slot' | 'break', id: number) => (
  store.selection?.kind === kind && store.selection.id === id
);

// --- Выделение интервала протягиванием мыши ---

let drag: DragState | null = null;
let suppressClickUntil = 0;
const dragRange = ref<Range | null>(null);

const isInRange = (cell: CellBase) => {
  const range = dragRange.value || store.selection;
  return !!range
    && cell.day === range.date
    && toMinutes(cell.start) < toMinutes(range.end)
    && toMinutes(cell.end) > toMinutes(range.start);
};

// Клик, которым закончилось протягивание, не должен открывать ячейку
const onCellClick = (action: () => void) => {
  if (performance.now() < suppressClickUntil) return;
  action();
};

const findCell = (target: EventTarget | null) => (
  (target as HTMLElement | null)?.closest<HTMLElement>('[data-range-date]') || null
);

const onPointerDown = (event: PointerEvent) => {
  if (store.busy || !store.canEdit || event.pointerType !== 'mouse' || event.button !== 0) return;
  const target = event.target as HTMLElement;
  if (!target.closest('.w-empty, .w-slot-info, .w-break')) return;
  const cell = findCell(target);
  if (!cell) return;
  const { rangeDate = '', rangeStart = '', rangeEnd = '' } = cell.dataset;
  drag = {
    date: rangeDate,
    start: rangeStart,
    end: rangeEnd,
    x: event.clientX,
    y: event.clientY,
    moved: false,
  };
};

const onPointerMove = (event: PointerEvent) => {
  if (!drag) return;
  // Левая кнопка отпущена за пределами таблицы
  if (event.buttons % 2 === 0) {
    drag = null;
    return;
  }
  const distance = Math.abs(event.clientY - drag.y) + Math.abs(event.clientX - drag.x);
  if (distance < DRAG_THRESHOLD) return;
  const cell = findCell(document.elementFromPoint(event.clientX, event.clientY));
  if (!cell || !tableWrap.value?.contains(cell) || cell.dataset.rangeDate !== drag.date) return;
  const { rangeStart = '', rangeEnd = '' } = cell.dataset;
  drag.moved = true;
  dragRange.value = {
    date: drag.date,
    start: toTime(Math.min(toMinutes(drag.start), toMinutes(rangeStart))),
    end: toTime(Math.max(toMinutes(drag.end), toMinutes(rangeEnd))),
  };
  event.preventDefault();
};

const onPointerUp = () => {
  const range = dragRange.value;
  if (drag?.moved && range) {
    suppressClickUntil = performance.now() + CLICK_SUPPRESS_MS;
    store.selectRange(range.date, range.start, range.end);
  }
  drag = null;
  dragRange.value = null;
};

onMounted(() => window.addEventListener('pointerup', onPointerUp));
onBeforeUnmount(() => window.removeEventListener('pointerup', onPointerUp));

return (_ctx: any,_cache: any) => {
  const _component_q_icon = _resolveComponent("q-icon")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", _hoisted_1, [
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(store).weekDays, (day) => {
        return (_openBlock(), _createElementBlock("button", {
          key: day,
          type: "button",
          "aria-pressed": String(day === _unref(store).date),
          "aria-label": _unref(formatDayTitle)(day),
          onClick: ($event: any) => (_unref(store).pickDay(day))
        }, [
          _createElementVNode("span", null, _toDisplayString(_unref(formatDate)(day, { weekday: 'short' })), 1),
          _createElementVNode("strong", null, _toDisplayString(_unref(formatDate)(day, { day: 'numeric' })), 1)
        ], 8, _hoisted_2))
      }), 128))
    ]),
    _createElementVNode("div", {
      ref_key: "tableWrap",
      ref: tableWrap,
      class: "w-table-wrap",
      onScroll: _cache[0] || (_cache[0] = ($event: any) => (_unref(store).closeRoomMenu())),
      onPointerdown: onPointerDown,
      onPointermove: onPointerMove
    }, [
      _createElementVNode("table", _hoisted_3, [
        _createElementVNode("thead", null, [
          _createElementVNode("tr", null, [
            _cache[5] || (_cache[5] = _createElementVNode("th", {
              scope: "col",
              class: "w-time-head"
            }, "Время", -1)),
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(dayHeads.value, ({ day, hours }) => {
              return (_openBlock(), _createElementBlock("th", {
                key: day,
                scope: "col",
                class: _normalizeClass(["w-day-head", { 'is-current': day === _unref(store).date }])
              }, [
                _createElementVNode("button", {
                  type: "button",
                  "aria-label": _unref(formatDayTitle)(day),
                  onClick: ($event: any) => (_unref(store).pickDay(day))
                }, [
                  _createElementVNode("span", null, _toDisplayString(_unref(formatDate)(day, { weekday: 'short' })), 1),
                  _createElementVNode("strong", null, _toDisplayString(_unref(formatDate)(day, { day: 'numeric' })), 1)
                ], 8, _hoisted_4),
                _createElementVNode("span", _hoisted_5, [
                  (hours.open.length)
                    ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                        _cache[1] || (_cache[1] = _createElementVNode("span", { class: "w-hours-label" }, "Работа ", -1)),
                        _createElementVNode("span", _hoisted_6, _toDisplayString(_unref(hoursText)(hours.open)), 1),
                        _cache[2] || (_cache[2] = _createElementVNode("br", null, null, -1)),
                        _cache[3] || (_cache[3] = _createElementVNode("span", { class: "w-hours-label" }, "Приём ", -1)),
                        _createElementVNode("span", _hoisted_7, _toDisplayString(_unref(hoursText)(hours.intake)), 1)
                      ], 64))
                    : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                        _createTextVNode("Закрыто")
                      ], 64)),
                  (hours.isException)
                    ? (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
                        _cache[4] || (_cache[4] = _createElementVNode("br", null, null, -1)),
                        _createTextVNode(" " + _toDisplayString(hours.label || 'Особый режим'), 1)
                      ], 64))
                    : _createCommentVNode("", true)
                ])
              ], 2))
            }), 128))
          ])
        ]),
        _createElementVNode("tbody", null, [
          (!rows.value.length)
            ? (_openBlock(), _createElementBlock("tr", _hoisted_8, [
                _createElementVNode("td", {
                  colspan: _unref(store).displayDays.length + 1,
                  class: "w-week-closed"
                }, _toDisplayString(_unref(store).displayDays.length === 1 ? 'В этот день' : 'В эти дни') + " филиал закрыт. Выберите другую дату. ", 9, _hoisted_9)
              ]))
            : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(rows.value, (row) => {
                  return (_openBlock(), _createElementBlock("tr", {
                    key: row.start,
                    style: _normalizeStyle({ height: `${row.height}px` })
                  }, [
                    _createElementVNode("th", _hoisted_10, _toDisplayString(row.start), 1),
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(row.cells, (cell) => {
                      return (_openBlock(), _createElementBlock("td", {
                        key: cell.day,
                        class: _normalizeClass(["w-day-cell", { 'w-range-selected': isInRange(cell), 'w-weekend': _unref(isWeekend)(cell.day) }]),
                        rowspan: cell.rowspan,
                        "data-range-date": cell.day,
                        "data-range-start": cell.start,
                        "data-range-end": cell.end
                      }, [
                        (cell.kind === 'slot')
                          ? (_openBlock(), _createElementBlock("div", {
                              key: 0,
                              class: _normalizeClass(["w-slot", { 'is-selected': isSelected('slot', cell.slot.id) }]),
                              "data-color": cell.status.color,
                              style: _normalizeStyle({ minHeight: `${cell.height}px` })
                            }, [
                              _createElementVNode("button", {
                                type: "button",
                                class: "w-room-button",
                                "data-room-slot": cell.slot.id,
                                "aria-label": `Кабинет ${_unref(store).roomNumber(cell.slot.roomId)}, `
                    + `${cell.slot.date} ${cell.slot.start}–${cell.slot.end}. Сменить кабинет`,
                                "aria-expanded": String(_unref(store).roomMenuSlotId === cell.slot.id),
                                onClick: ($event: any) => (_unref(store).toggleRoomMenu(cell.slot.id))
                              }, [
                                _createElementVNode("span", _hoisted_14, _toDisplayString(_unref(store).roomNumber(cell.slot.roomId)), 1),
                                _createVNode(_component_q_icon, { name: "expand_more" })
                              ], 8, _hoisted_13),
                              _createElementVNode("button", {
                                type: "button",
                                class: "w-slot-info",
                                "aria-label": `${cell.slot.start}–${cell.slot.end}. `
                    + `${cell.status.label}. Открыть приём`,
                                onClick: ($event: any) => (onCellClick(() => _unref(store).selectSlot(cell.slot.id)))
                              }, [
                                _createElementVNode("span", _hoisted_16, _toDisplayString(cell.status.full), 1),
                                _createElementVNode("span", _hoisted_17, _toDisplayString(cell.status.short), 1),
                                _createElementVNode("span", _hoisted_18, _toDisplayString(cell.slot.start) + "–" + _toDisplayString(cell.slot.end), 1)
                              ], 8, _hoisted_15)
                            ], 14, _hoisted_12))
                          : (cell.kind === 'break')
                            ? (_openBlock(), _createElementBlock("button", {
                                key: 1,
                                type: "button",
                                class: "w-break",
                                style: _normalizeStyle({ minHeight: `${cell.height}px` }),
                                "aria-label": `${cell.rest.label} ${cell.rest.start}–${cell.rest.end}`,
                                onClick: ($event: any) => (onCellClick(() => _unref(store).selectBreak(cell.rest.id)))
                              }, [
                                _createVNode(_component_q_icon, {
                                  name: breakIcon(cell.rest.label)
                                }, null, 8, ["name"]),
                                _createElementVNode("span", null, _toDisplayString(cell.rest.label), 1)
                              ], 12, _hoisted_19))
                            : (cell.kind === 'empty')
                              ? (_openBlock(), _createElementBlock("button", {
                                  key: 2,
                                  type: "button",
                                  class: "w-empty",
                                  style: _normalizeStyle({ minHeight: `${cell.height}px` }),
                                  disabled: !_unref(store).canEdit,
                                  "aria-label": `Добавить приём ${cell.day} ${cell.start}–${cell.end}`,
                                  onClick: ($event: any) => (onCellClick(() => _unref(store).selectRange(cell.day, cell.start, cell.end)))
                                }, [...(_cache[6] || (_cache[6] = [
                                  _createElementVNode("span", { "aria-hidden": "true" }, "+", -1)
                                ]))], 12, _hoisted_20))
                              : (_openBlock(), _createElementBlock("div", {
                                  key: 3,
                                  class: "w-closed-cell",
                                  style: _normalizeStyle({ minHeight: `${cell.height}px` }),
                                  "aria-label": cell.reason
                                }, _toDisplayString(cell.text), 13, _hoisted_21))
                      ], 10, _hoisted_11))
                    }), 128))
                  ], 4))
                }), 128)),
                _createElementVNode("tr", _hoisted_22, [
                  _createElementVNode("th", _hoisted_23, _toDisplayString(_unref(store).viewHours.to), 1),
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(store).displayDays, (day) => {
                    return (_openBlock(), _createElementBlock("td", { key: day }))
                  }), 128))
                ])
              ], 64))
        ])
      ])
    ], 544)
  ], 64))
}
}

})